<template>
    <div id="workstation-profiles-on">
        <div
            class="timer"
            v-if="timer && !timer.finished"
        >
            <product-timer
                :loading="loading"
                @finish="onFinish"
                @pause="onPause"
                @unpause="startTimer(true)"
                :saving="saving"
                :pausing-or-restarting="pausingOrRestarting"
                :timer="timer"
                :all-finished="allFinished"
            />
        </div>
        <div class="paintlist-entry">
            <ui5-label for="sawmachine-profile">
                {{ trans('pages.workstation_profiles_on.scan_label') }}
            </ui5-label>
            <ui5-input
                type="Text"
                :disabled="loading || (timer && !timer.finished)"
                :value="paintlistId"
                @input="paintlistId = $event.target.value"
                @blur="loadData"
                @keyup.enter="loadData"
                :placeholder="
                    trans('pages.workstation_profiles_on.scan_placeholder')
                "
            />
            <div class="camera-icon">
                <ui5-button
                    @click="scan"
                    :disabled="loading || (timer && !timer.finished)"
                >
                    <i class="fa fa-camera fa-2x" />
                </ui5-button>
            </div>
        </div>
        <div
            class="paintlist-view"
            v-if="paintlist"
        >
            <div class="information-item">
                <div class="label">
                    {{ trans('pages.workstation_profiles_on.paintlist') }}
                </div>
                <div>{{ paintlist.ID }}</div>
            </div>
            <div class="information-item">
                <div class="label">
                    {{ trans('pages.workstation_profiles_on.paintworkform') }}
                </div>
                <div>{{ paintlist.paintworkform }}</div>
            </div>
            <div
                class="information-item-list"
                v-if="timer"
            >
                <div class="label">
                    {{ trans('pages.workstation_profiles_on.products') }}
                </div>
                <ul>
                    <li
                        v-for="product in timer.products"
                        :key="'product.' + product.productNr"
                    >
                        <div class="product-nr">{{ product.productNr }}</div>
                        <div
                            class="product-description"
                            v-html="product.description"
                        />
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
<script>
import Spinner from '../components/loader/Spinner';
import optimizedPaintListApi from '../api/optimizedPaintList';
import productionTimesApi from '../api/productionTimes';
import NavigationFunctionsMixin from '../mixins/navigation-functions';
import { showErrorMessage, showInfoMessage } from '../dialogs';
import workstationApi from '../api/workstation';
import ProductTimer from '../components/scanProducts/ProductTimer';
import { optimizedPaintListMocker } from '../shared/qrMockers';
import { createAggregatedTimer } from '../shared/timers';
import localization from '../mixins/localization';

const defaultData = {
    pausingOrRestarting: false,
    timer: null,
    workstation: null,
    saving: false,
    loadingTimer: false,
    loadingPaintlist: false,
    paintlistId: '',
    paintlist: null,
};
export default {
    mixins: [NavigationFunctionsMixin, localization],
    components: { Spinner, ProductTimer },
    data() {
        return {
            ...defaultData,
        };
    },
    mounted() {
        workstationApi
            .fetchByCustomView('#workstationprofileson')
            .then((response) => {
                this.workstation = response.data.data;
            });

        this.$bus.$on('barcode-scan', (data) => {
            this.paintlistId = data;
            this.loadData();
        });

        this.$bus.$on('qr_scanner.on_scan', (key, value) => {
            if (key === 'workstationprofileson') {
                this.onScan(value);
            }
        });
    },
    computed: {
        Config: () => Config,
        loading() {
            return this.loadingPaintlist || this.loadingTimer;
        },
        allFinished() {
            return true;
        },
    },
    beforeRouteLeave(to, from, next) {
        if (this.timer && !this.timer.finished) {
            this.onFinish();
        }
        next();
    },
    methods: {
        finish(pause) {
            const recordIds = this.timer.products.map(
                (x) => x.historyRecord.ID
            );
            return productionTimesApi
                .finishOptimizedPaintList(this.paintlistId, recordIds, pause)
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.workstation_profiles_on.errors.unknown_save_error'
                        )
                    );
                    this.timer.finished = false;
                });
        },
        onFinish() {
            this.saving = true;
            this.finish(false)
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.workstation_profiles_on.success.production_time_saved'
                        )
                    );
                    Object.keys(defaultData).forEach((key) => {
                        this[key] = JSON.parse(
                            JSON.stringify(defaultData[key])
                        );
                    });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onPause() {
            this.pausingOrRestarting = true;
            this.finish(true)
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.workstation_profiles_on.success.production_time_saved'
                        )
                    );
                })
                .finally(() => {
                    this.pausingOrRestarting = false;
                });
        },
        startTimer(restart = false) {
            if (restart) {
                this.pausingOrRestarting = true;
            } else {
                this.loadingTimer = true;
            }
            productionTimesApi
                .fetchOptimizedPaintList(this.paintlistId, this.workstation.id)
                .then((response) => {
                    const timerData = response.data.data;
                    if (timerData.missingMinutes.length > 0) {
                        const str = timerData.missingMinutes
                            .map((x) => '#' + x)
                            .join(', ');
                        const plural =
                            timerData.missingMinutes.length == 1 ? '' : 's';
                        showInfoMessage(
                            this.trans(
                                'pages.workstation_profiles_on.info.missing_target_times',
                                {
                                    plural: plural,
                                    types: str,
                                }
                            )
                        );
                    }
                    this.timer = createAggregatedTimer(timerData);
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.workstation_profiles_on.errors.timer_start_error'
                        )
                    );
                })
                .finally(() => {
                    if (restart) {
                        this.pausingOrRestarting = false;
                    } else {
                        this.loadingTimer = false;
                    }
                });
        },
        scan() {
            this.$bus.$emit('qr_scanner.start', 'workstationprofileson', {
                singleScan: true,
                defaultCamera: 'front',
                mocker: optimizedPaintListMocker,
            });
        },
        onScan(value) {
            let decoded = null;
            try {
                decoded = JSON.parse(value);
            } catch (error) {}

            if (decoded && decoded.context === 'optimizedPaintList') {
                this.paintlistId = decoded.optimizedPaintListId;
                this.loadData();
            } else {
                showErrorMessage(
                    this.trans(
                        'pages.workstation_profiles_on.errors.invalid_qr_code'
                    ),
                    null,
                    1500
                );
            }
        },
        loadData() {
            if (this.loadingPaintlist) {
                return;
            }

            if (this.paintlistId.toString().length == 0) {
                return showErrorMessage(
                    this.trans(
                        'pages.workstation_profiles_on.errors.invalid_paintlist_number'
                    )
                );
            }

            this.loadingPaintlist = true;

            optimizedPaintListApi
                .fetch(this.paintlistId)
                .then((response) => {
                    const paintlist = response.data.data;
                    if (paintlist.reference === null) {
                        paintlist.reference = paintlist.ID;
                    }
                    this.paintlistId = paintlist.ID;
                    this.paintlist = paintlist;
                    this.startTimer();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.workstation_profiles_on.errors.paintlist_load_error'
                        )
                    );
                })
                .finally(() => {
                    this.loadingPaintlist = false;
                });
        },
    },
};
</script>
